import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

// Historico unificado (view link_history): paginacao e filtros no servidor.

export const HISTORY_PAGE_SIZE = 20;

export type HistoryPeriod = "7d" | "30d" | "90d" | "all";

export interface HistoryFilters {
  readonly marketplace: string;
  readonly origin: string;
  readonly destination: string;
  readonly linkType: string;
  readonly period: HistoryPeriod;
}

export const EMPTY_HISTORY_FILTERS: HistoryFilters = {
  marketplace: "",
  origin: "",
  destination: "",
  linkType: "",
  period: "30d",
};

export interface LinkHistoryRow {
  readonly id: string;
  readonly created_at: string;
  readonly marketplace_code: string | null;
  readonly origin: string | null;
  readonly destination: string | null;
  readonly link_type: "short" | "direct" | null;
  readonly short_code: string | null;
  readonly destination_url: string | null;
  readonly original_url: string | null;
  readonly title: string | null;
  readonly image_url: string | null;
  readonly price_cents: number | null;
  readonly coupon: string | null;
  readonly status: "converted" | "error" | "ignored" | "pending";
  readonly error_code: string | null;
}

export interface LinkHistoryPage {
  readonly rows: readonly LinkHistoryRow[];
  readonly total: number;
}

const PERIOD_DAYS: Record<Exclude<HistoryPeriod, "all">, number> = { "7d": 7, "30d": 30, "90d": 90 };

/** Inicio do periodo em ISO (null = sem corte). */
export function periodStart(period: HistoryPeriod, now: Date = new Date()): string | null {
  if (period === "all") {
    return null;
  }
  const start = new Date(now.getTime() - PERIOD_DAYS[period] * 24 * 60 * 60 * 1000);
  return start.toISOString();
}

/** Escapa curingas do ilike para busca literal do destino digitado. */
export function escapeLike(value: string): string {
  return value.replace(/[\\%_]/g, (char) => `\\${char}`);
}

interface FilterableQuery<Q> {
  eq(column: string, value: string): Q;
  ilike(column: string, pattern: string): Q;
  gte(column: string, value: string): Q;
}

/** Aplica os filtros preenchidos na query do PostgREST (vazio = sem filtro). */
export function applyHistoryFilters<Q extends FilterableQuery<Q>>(query: Q, filters: HistoryFilters, now?: Date): Q {
  let next = query;
  if (filters.marketplace) next = next.eq("marketplace_code", filters.marketplace);
  if (filters.origin) next = next.eq("origin", filters.origin);
  if (filters.linkType) next = next.eq("link_type", filters.linkType);
  if (filters.destination.trim()) next = next.ilike("destination", `%${escapeLike(filters.destination.trim())}%`);
  const start = periodStart(filters.period, now);
  if (start) next = next.gte("created_at", start);
  return next;
}

async function fetchHistory(filters: HistoryFilters, page: number): Promise<LinkHistoryPage> {
  const from = page * HISTORY_PAGE_SIZE;
  const base = supabase.from("link_history").select("*", { count: "exact" });
  const { data, error, count } = await applyHistoryFilters(base, filters)
    .order("created_at", { ascending: false })
    .range(from, from + HISTORY_PAGE_SIZE - 1);
  if (error) {
    throw error;
  }
  return { rows: (data ?? []) as LinkHistoryRow[], total: count ?? 0 };
}

export function useLinkHistory(filters: HistoryFilters, page: number) {
  return useQuery<LinkHistoryPage, Error>({
    queryKey: ["link-history", filters, page],
    queryFn: () => fetchHistory(filters, page),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}
